import React from 'react';
import Layout from '../layout/Layout';
import CtaBand from '../layout/CtaBand';
import Section from '../ui/Section';
import Container from '../ui/Container';
import Button from '../ui/Button';
import Sparkle from '../ui/Sparkle';
import Eyebrow from '../ui/Eyebrow';
import Marquee from '../ui/Marquee';
import Reveal from '../ui/Reveal';
import { ShopstarGoCard, ShopstarGoBand } from '../ui/ShopstarGo';
import { APP, shopstarGoHref } from '../layout/siteData';
import { PRICING_VARIANTS, DEFAULT_VARIANT } from '../../content/pricing';
import { trackGoClick } from '../../lib/track';

// Reusable styled link for the inline Shopstar Go nudges woven through the page.
const GoLink = ({ medium, children }) => {
  const href = shopstarGoHref(medium);
  return (
    <a
      href={href}
      onClick={() => trackGoClick(href, children)}
      data-testid={`pricing-go-link-${medium}`}
      className="font-bold text-ink underline decoration-amber decoration-2 underline-offset-2 transition-colors hover:text-amber"
    >
      {children}
    </a>
  );
};

const Check = ({ on = 'light' }) => (
  <svg className={on === 'dark' ? 'text-amber flex-none' : 'text-ink flex-none'} width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <circle cx="12" cy="12" r="12" fill="currentColor" opacity="0.15" />
    <path d="M7 12.5l3.2 3.2L17 8.5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const FAQS = [
  ['What happens after my 14-day free trial?', 'Your store is placed on hold until you choose a package, at which point we reactivate it. Not sure which to pick? We’re always here to help.'],
  ['Can I change plans at any time?', 'Our billing is really flexible. You have full control over your package — upgrade or downgrade at any time.'],
  ['Can I cancel whenever I want?', 'Of course. You subscribe month-to-month. No long-term contracts are involved.'],
  ['Can I sell bookings, memberships or tickets?', (
    <>
      Those product types live on <strong className="text-ink">Shopstar Go</strong>, our new
      platform that sells products, downloads, bookings, memberships and tickets from one simple
      page — free to start, with no monthly fee. <GoLink medium="pricing_faq">Explore Shopstar Go →</GoLink>
    </>
  )],
];

const INCLUDES = [
  ['Beautiful by default', 'No-code drag-and-drop designer and ready-made themes.'],
  ['Sell everywhere', 'Facebook, Instagram, Google and WhatsApp, built in.'],
  ['Local payments', 'SnapScan, Yoco, Ozow, Payflex, Paystack and more.'],
  ['Shipping sorted', 'Bob Go and flexible shipping options out the box.'],
  ['Digital products', 'Sell downloads and digital goods with ease.'],
  ['Real support', '100% South African Customer Success Team.'],
];

// Success-based transaction fees, tiered by monthly gross order volume.
// [revenue band, fee] — ordered low → high revenue.
const FEE_TIERS = [
  ['R0 – R50 000', '1.7%'],
  ['R50 000 – R500 000', '1.5%'],
  ['R500 000 – R1 000 000', '1.0%'],
  ['R1 000 000 – R1 500 000+', '0.05%'],
];

const Pricing = ({ variant = DEFAULT_VARIANT }) => {
  const data = PRICING_VARIANTS[variant] || PRICING_VARIANTS[DEFAULT_VARIANT];
  const { promo, tiers, features } = data;

  return (
    <Layout>
      <section className="relative overflow-hidden bg-sky text-center">
        <Sparkle size={120} color="#95a8e6" className="pointer-events-none absolute -top-3 right-[8%] opacity-25 hidden sm:block" />
        <Sparkle size={54} color="#ffb442" className="pointer-events-none absolute bottom-[10%] left-[7%] opacity-80" />
        <Container className="relative py-16 md:py-20">
          <Eyebrow className="justify-center">Pricing &amp; features</Eyebrow>
          <h1 className="mt-4 text-h1 font-extrabold text-ink">Build your shop.<br /><span className="text-ink">No hidden costs.</span></h1>
          <p className="mt-5 mx-auto max-w-[52ch] text-lead text-body">Simple, flexible month-to-month pricing. Choose a plan based on how many products you’re selling — and change it whenever you like.</p>
          {promo && (
            <div className="inline-flex items-center gap-2 mt-7 bg-brand text-white font-extrabold px-5 py-2.5 rounded-full shadow-soft">
              <Sparkle size={16} color="#ffffff" /> Save 50% on your first two months of any plan
            </div>
          )}
        </Container>
      </section>

      <Section tone="cream" pad="lg" container={false}>
        <div className="mx-auto w-full max-w-[1680px] px-5 lg:px-8">
        <div className="grid lg:grid-cols-4 gap-6 items-start max-w-[480px] lg:max-w-none mx-auto">
          <ShopstarGoCard />
          {tiers.map((t) => {
            const dark = t.popular;
            return (
              <Reveal key={t.name} className={`relative rounded-4xl p-9 transition-all hover:-translate-y-1 ${dark ? 'bg-ink shadow-card' : 'bg-white border border-line hover:shadow-soft'}`}>
                {t.popular && <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-amber text-ink text-[0.78rem] font-extrabold uppercase tracking-wide px-4 py-1.5 rounded-full whitespace-nowrap">Most popular</span>}
                <h3 className={`text-h3 font-extrabold ${dark ? 'text-white' : 'text-ink'}`}>{t.name}</h3>
                <p className={dark ? 'text-white/60 font-semibold mt-1' : 'text-muted font-semibold mt-1'}>{t.cap}</p>
                <div className="flex items-baseline gap-2 mt-6">
                  {promo && <span className={`line-through font-bold text-lg ${dark ? 'text-white/50' : 'text-muted'}`}>{t.full}</span>}
                  <span className={`text-[clamp(2.4rem,5vw,3rem)] font-extrabold tracking-tight leading-none ${dark ? 'text-white' : 'text-ink'}`}>{promo ? t.promo : t.full}</span>
                  <span className={dark ? 'text-white/60 font-bold' : 'text-muted font-bold'}>/mo</span>
                </div>
                <p className={`text-sm mt-2 ${dark ? 'text-white/50' : 'text-muted'}`}>{promo ? `${t.full}/mo after your first two months` : 'Billed monthly · cancel anytime'}</p>
                <div className="mt-6"><Button href={APP.signUp} full variant={dark ? 'primary' : 'secondary'} icon={false}>Start my shop</Button></div>
                <p className={`text-center text-sm mt-3 ${dark ? 'text-white/50' : 'text-muted'}`}>Try it FREE for 14 days</p>
                <ul className="mt-7 flex flex-col gap-3">
                  {features.map((f) => (
                    <li key={f} className={`flex items-center gap-2.5 font-medium ${dark ? 'text-white/85' : 'text-body'}`}><Check on={dark ? 'dark' : 'light'} />{f}</li>
                  ))}
                </ul>
              </Reveal>
            );
          })}
        </div>
        <p className="text-center mt-9 text-muted font-semibold">Cancel whenever you want · No long-term contracts · No setup fees</p>
        <p data-testid="pricing-shopstar-go-hint" className="text-center mt-4 text-muted">
          Selling downloads, bookings, memberships or tickets?{' '}
          <GoLink medium="pricing_under_cards">Shopstar Go is free to start →</GoLink>
        </p>
        </div>
      </Section>

      <Section tone="white" pad="lg" id="transaction-fees">
        <div className="max-w-[52ch] mb-12">
          <Eyebrow>Success based pricing</Eyebrow>
          <h2 className="mt-4 text-h2 font-extrabold text-ink">Transaction fees</h2>
          <p className="mt-3 text-lead text-body">
            Our pricing is competitive and we are on average more affordable than our peers.
            As a 100% local business, we understand how an affordable solution is key to our
            customers’ businesses.
          </p>
        </div>
        <div data-testid="transaction-fees-table" className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEE_TIERS.map(([range, rate], i) => (
            <Reveal
              key={range}
              delay={(i % 4) * 60}
              className="bg-cream rounded-3xl border border-line p-8"
            >
              <Sparkle size={22} color="#ffb442" />
              <span
                data-testid={`fee-tier-${i}-range`}
                className="block mt-4 text-xs font-extrabold uppercase tracking-[0.14em] text-muted"
              >
                Revenue of {range}
              </span>
              <strong
                data-testid={`fee-tier-${i}-rate`}
                className="block mt-3 text-[clamp(2rem,4vw,2.6rem)] font-extrabold leading-none text-ink"
              >
                {rate}
              </strong>
            </Reveal>
          ))}
        </div>
        <p className="mt-6 text-sm text-muted">
          * Transaction fees are charged per month based on gross total order volume.
        </p>
      </Section>

      <Marquee tone="ink" items={['No hidden costs', 'Low transaction fees', 'No credit card required', 'Cancel anytime', 'All features, every plan']} />

      <ShopstarGoBand />

      <Section tone="white" pad="lg">
        <div className="max-w-[42ch] mb-12">
          <Eyebrow>Every plan includes</Eyebrow>
          <h2 className="mt-4 text-h2 font-extrabold text-ink">Everything you need, on every plan.</h2>
          <p className="mt-3 text-lead text-body">No feature gates. Every Shopstar plan comes fully loaded — you only pick how many products you sell.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {INCLUDES.map(([t, d], i) => (
            <Reveal key={t} delay={(i % 3) * 60} className="bg-cream rounded-3xl border border-line p-8">
              <Sparkle size={22} color="#ffb442" />
              <strong className="block text-lg text-ink mt-4 mb-2">{t}</strong>
              <span className="text-body">{d}</span>
            </Reveal>
          ))}
        </div>
      </Section>

      <Section tone="cream" pad="lg">
        <div className="max-w-[42ch] mb-12"><Eyebrow>Good to know</Eyebrow><h2 className="mt-4 text-h2 font-extrabold text-ink">Questions, answered.</h2></div>
        <div className="grid md:grid-cols-3 gap-5">
          {FAQS.map(([q, a]) => (
            <div key={q} className="bg-white rounded-3xl border border-line p-8">
              <h3 className="text-lg font-extrabold text-ink mb-3">{q}</h3>
              <p className="text-body">{a}</p>
            </div>
          ))}
        </div>
      </Section>

      <CtaBand title="Pick a plan and start selling." secondary={{ label: 'Talk to us', href: '/contact' }} />
    </Layout>
  );
};

export default Pricing;
